import { formatPriceListPrice, type LicenceCatalog, type LicenceSection } from "@xop/dashboard-shared"
import type { JSX } from "react"

import { FeatureTilePicker, type FeatureTileItem } from "@crm-src/components/feature-tiles"

/**
 * The licence builder's feature picker.
 *
 * THE CATALOGUE IS CRM ▸ PRICING (2026-09-15). A tile is a priced feature —
 * "Mass Notification, 700-2341-001, $139 per port" — not an appliance key,
 * because the person building a licence is reading off what the customer
 * BOUGHT. What a licence file names is the appliance key each feature is
 * LINKED to (`ProductFeature.licence_key`, curated on the Pricing page), so
 * toggling a tile reports that key, and a feature nobody has linked yet is
 * shown greyed and cannot be picked: it is sold, but the appliance would not
 * know what it means.
 *
 * A licence may already name a key no Pricing row links — one built before the
 * catalogue moved, or a key nobody sells separately. That key is NOT hidden
 * (the licence would become unreadable, and un-renewable): it appears as a
 * greyed "legacy" tile under Platform Extensions, selected, and can still be
 * switched OFF. A key the appliance itself does not know is shown the same
 * way, defensively.
 *
 * The look is the shared `FeatureTilePicker` — the same control the Edit
 * Agreement modal uses — so "which features does this customer have" reads
 * alike wherever it is asked. The appliance key is never printed on a linked
 * tile; only a legacy tile names its key, because the key is all there is.
 */

const SECTION_HINTS: Record<LicenceSection, string> = {
  value_added_services: "Priced in CRM ▸ Pricing.",
  platform_extensions: "Everything else the appliance may run.",
}

export const UNLINKED_NOTE = "Not linked to an appliance feature yet — set it in CRM ▸ Pricing."

export interface LicenceFeaturePickerProps {
  catalog: LicenceCatalog
  selectedKeys: string[]
  disabled: boolean
  onToggle: (key: string) => void
}

/** "web_conferencing" → "Web Conferencing", for a legacy key with no row to name it. */
function humaniseKey(key: string): string {
  return key
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

/**
 * The tiles: one per Pricing row (linked or not), then one per selected key no
 * row links. Exported so the mapping — which key a tile reports, what cannot
 * be picked, what is legacy — is pinned without rendering.
 */
export function licencePickerItems(catalog: LicenceCatalog, selectedKeys: readonly string[]): FeatureTileItem[] {
  const selected = new Set(selectedKeys)
  const linked = new Set<string>()
  const items: FeatureTileItem[] = []

  for (const row of catalog.features) {
    const price = formatPriceListPrice({
      unit: row.pricing.unit,
      pricing_mode: row.pricing.pricingMode,
      price_cents: row.pricing.priceCents,
      percent_rate: row.pricing.percentRate,
      currency: row.pricing.currency,
    })
    const priceLine = row.pricing.partNumber ? `${row.pricing.partNumber} · ${price}` : price
    if (row.licenceKey) {
      linked.add(row.licenceKey)
      items.push({
        id: row.licenceKey,
        name: row.name,
        section: row.section,
        caption: priceLine,
        on: selected.has(row.licenceKey),
        selectable: true,
        retired: !row.pricing.active,
      })
    } else {
      items.push({
        id: `unlinked:${row.id}`,
        name: row.name,
        section: row.section,
        caption: UNLINKED_NOTE,
        on: false,
        selectable: false,
        testId: "licence-feature-unlinked",
      })
    }
  }

  for (const key of selectedKeys) {
    if (linked.has(key)) continue
    items.push({
      id: key,
      name: humaniseKey(key),
      section: "platform_extensions",
      caption: `Legacy appliance key ${key} — not in Pricing`,
      on: true,
      selectable: true,
      muted: true,
      searchText: key,
      testId: "licence-feature-legacy",
    })
  }

  return items
}

export function LicenceFeaturePicker({ catalog, selectedKeys, disabled, onToggle }: LicenceFeaturePickerProps): JSX.Element {
  return (
    <FeatureTilePicker
      items={licencePickerItems(catalog, selectedKeys)}
      disabled={disabled}
      onToggle={onToggle}
      hints={SECTION_HINTS}
      groupTestIdPrefix="licence-feature-group"
    />
  )
}
